import React from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Button from "@material-ui/core/Button";
import { useDispatch, useSelector } from "react-redux";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorOutlineRoundedIcon from "@material-ui/icons/ErrorOutlineRounded";
import {
  setDniAction,
  setIdCrmAction,
  setIdLoginAction,
  setIdUserAction,
  setSearchTabAction,
} from "../../../../../reducers";
import { KEY_ENTER, SEARCH_TAB_ID_CRM, SEARCH_TAB_LOGIN, SEARCH_TAB_NIF } from "../../../../../utils/constants";
import { Divider } from "@material-ui/core";
import { useIntl } from "react-intl";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    padding: theme.spacing(4, 2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
  },
  title: {
    textTransform: "upperCase",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
  },
  button: theme.classes.mainButton,
  secondaryButton: theme.classes.secondaryButton,
  error: {
    color: "red",
  },
  tabBorder: {
    height: 3,
    backgroundColor: theme.palette.primary.main,
  },
  tabSelected: {
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    width: "100%",
    height: 56,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
  tabUnselected: {
    width: "100%",
    height: 56,
    border: `2px solid ${theme.palette.primary.main}`,
    borderBottom: "none",
  },
}));

export const UserSearchComponent = (props) => {
  const { title, handleSubmit, setter } = props;
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.loading);
  const storedIdCrm = useSelector((state) => state.idCrm);
  const storedDniNif = useSelector((state) => state.dni);
  const storedIdLogin = useSelector((state) => state.idLogin);
  const storedTab = useSelector((state) => state.searchTab);
  const tenant = useSelector((state) => state.tenant);

  const handleCrmId = (value) => {
    if (!value) {
      dispatch(setIdCrmAction(""));
      dispatch(setIdUserAction(""));
    }
    dispatch(setIdCrmAction(value));
  };

  const handleDni = (value) => {
    dispatch(setDniAction(value.toUpperCase()));
  };

  const handleLogin = (value) => {
    dispatch(setIdLoginAction(value.toUpperCase()));
  };

  const handleSendForm = (event) => {
    const { key } = event;

    if (
      tenant &&
      key === KEY_ENTER &&
      ((storedIdCrm && storedTab === SEARCH_TAB_ID_CRM) ||
        (storedDniNif && storedTab === SEARCH_TAB_NIF) ||
        (storedIdLogin && storedTab === SEARCH_TAB_LOGIN))
    ) {
      handleSubmit();
    }
  };

  const handleTabClick = (active) => {
    dispatch(setSearchTabAction(active));
  };

  const handleEraseInfo = () => {
    if (storedTab === SEARCH_TAB_ID_CRM) {
      dispatch(setIdCrmAction(""));
      dispatch(setIdUserAction(""));
    } else if (storedTab === SEARCH_TAB_LOGIN) {
      dispatch(setIdLoginAction(""));
    } else {
      dispatch(setDniAction(""));
    }
    setter();
  };

  return (
    <div className={classes.pageContainer}>
      {title && (
        <Typography variant={"h4"} className={classes.title}>
          {title}
        </Typography>
      )}
      <Grid container direction={"column"}>
        <Grid item container xs>
          <Grid item xs={12} md={3} lg={2}>
            <Button
              className={storedTab === SEARCH_TAB_ID_CRM ? classes.tabSelected : classes.tabUnselected}
              onClick={() => handleTabClick(SEARCH_TAB_ID_CRM)}
            >
              {intl.formatMessage({ id: "custom.id_crm_tab" })}
            </Button>
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <Button
              className={storedTab === SEARCH_TAB_LOGIN ? classes.tabSelected : classes.tabUnselected}
              onClick={() => handleTabClick(SEARCH_TAB_LOGIN)}
            >
              {intl.formatMessage({ id: "custom.login" })}
            </Button>
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <Button
              className={storedTab === SEARCH_TAB_NIF ? classes.tabSelected : classes.tabUnselected}
              onClick={() => handleTabClick(SEARCH_TAB_NIF)}
            >
              {intl.formatMessage({ id: "custom.dni" })}
            </Button>
          </Grid>
        </Grid>
        <Divider className={classes.tabBorder} />
      </Grid>

      <Grid container spacing={2}>
        {storedTab === SEARCH_TAB_ID_CRM && (
          <Grid item xs={12} md={3} lg={2}>
            <TextField
              label={intl.formatMessage({ id: "custom.id_crm" })}
              value={storedIdCrm}
              variant="outlined"
              onChange={(e) => handleCrmId(e.target.value)}
              onKeyDown={handleSendForm}
            />
          </Grid>
        )}
        {storedTab === SEARCH_TAB_LOGIN && (
          <Grid item xs={12} md={3} lg={2}>
            <TextField
              label={intl.formatMessage({ id: "custom.login" })}
              value={storedIdLogin}
              variant="outlined"
              onChange={(e) => handleLogin(e.target.value)}
              onKeyDown={handleSendForm}
            />
          </Grid>
        )}
        {storedTab === SEARCH_TAB_NIF && (
          <Grid item xs={12} md={3} lg={2}>
            <TextField
              label={intl.formatMessage({ id: "custom.dni" })}
              value={storedDniNif}
              variant="outlined"
              onChange={(e) => handleDni(e.target.value)}
              onKeyDown={handleSendForm}
            />
          </Grid>
        )}
        <Grid item xs={12} md={3} lg={4} className={classes.buttonContainer}>
          <Button
            className={classes.button}
            disabled={
              (!storedIdCrm && storedTab === SEARCH_TAB_ID_CRM) ||
              (!storedDniNif && storedTab === SEARCH_TAB_NIF) ||
              (!storedIdLogin && storedTab === SEARCH_TAB_LOGIN) ||
              !tenant
            }
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SearchRoundedIcon />}
          >
            {intl.formatMessage({ id: "ra.action.search" })}
          </Button>
          <Button
            className={classes.secondaryButton}
            onClick={handleEraseInfo}
            startIcon={<HighlightOffRoundedIcon />}
            disabled={
              (storedTab === SEARCH_TAB_ID_CRM && !storedIdCrm) ||
              (!storedDniNif && storedTab === SEARCH_TAB_NIF) ||
              (!storedIdLogin && storedTab === SEARCH_TAB_LOGIN)
            }
          >
            {intl.formatMessage({ id: "ra.action.erase" })}
          </Button>
        </Grid>
        {loading && (
          <Grid item xs={12} md={4} lg={2}>
            <CircularProgress color="primary" />
          </Grid>
        )}
        {!tenant && (
          <Grid item xs className={classes.error}>
            <ErrorOutlineRoundedIcon />
            <Typography variant={"body2"}>{intl.formatMessage({ id: "custom.tenant.tenant_mandatory" })}</Typography>
          </Grid>
        )}
      </Grid>
    </div>
  );
};
